import React, { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { api_url } from '../../constant/constant';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
} from 'recharts';

const COLORS = ['#34d399', '#f87171', '#3b82f6', '#fbbf24'];

const PhishingCampaignsProgress = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedPhishing, setSelectedPhishing] = useState('');
  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [data, setData] = useState({
    opened_vs_clicked: { opened: 0, clicked: 0 },
    opened_vs_submitted: { opened: 0, submitted: 0 },
    daily_opened_vs_not_opened: [{ opened: 0, not_opened: 0 }],
    activity_summary_by_date: {
      opened: [],
      clicked: [],
      submitted: [],
    },
    campaign_name: '',
  });

  const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/admin/main/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result?.data) setDepartments(result.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchPhishing = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/phishing-campaigns`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result?.data?.data) setCampaigns(result.data.data);
    } catch (err) {
      console.error('Error fetching phishing campaigns:', err);
    }
  };

  const fetchData = async () => {
    if (!selectedPhishing) {
      setError(t('PleaseSelectPhishingAndDepartment'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${api_url}/report/phishing/campaign-comparison?campaign_id=${selectedPhishing}&department_id=${selectedDepartment}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if ([401, 403, 429].includes(response.status)) {
        localStorage.removeItem('userToken');
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.status === 'success') {
        setData(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch campaign data.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPhishing();
    fetchDepartments();
  }, []);

  const toPieData = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: key.replace(/_/g, ' '),
        value,
      }));
  };

  const hasPieData = (obj) => {
    return toPieData(obj).length > 0;
  };

  const lineChartData = [];
  const datesSet = new Set();

  if (data) {
    (data.activity_summary_by_date?.opened || []).forEach((item) => datesSet.add(item.date));
    (data.activity_summary_by_date?.clicked || []).forEach((item) => datesSet.add(item.date));
    (data.activity_summary_by_date?.submitted || []).forEach((item) => datesSet.add(item.date));

    Array.from(datesSet)
      .sort()
      .forEach((date) => {
        const openedItem = (data.activity_summary_by_date?.opened || []).find(
          (d) => d.date === date,
        );
        const clickedItem = (data.activity_summary_by_date?.clicked || []).find(
          (d) => d.date === date,
        );
        const submittedItem = (data.activity_summary_by_date?.submitted || []).find(
          (d) => d.date === date,
        );

        lineChartData.push({
          date,
          opened: openedItem ? openedItem.count : 0,
          clicked: clickedItem ? clickedItem.count : 0,
          submitted: submittedItem ? submittedItem.count : 0,
        });
      });
  }

  return (
    <div className='container py-4'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h2>
          {t('PhishingCampaignSummary')}: {data?.campaign_name || t('NoData')}
        </h2>
        <Button variant='primary' onClick={() => navigate(-1)}>
          {t('Back')}
        </Button>
      </div>

      <div className='mb-4 p-3 card'>
        <div className='p-3' style={{ backgroundColor: '#f0f4f8', borderRadius: '16px' }}>
          <div className='row g-3 align-items-center'>
            <div className='col-md-4'>
              <label className='form-label'>{t('SelectPhishing')}</label>
              <select
                className='form-select'
                value={selectedPhishing}
                onChange={(e) => setSelectedPhishing(e.target.value)}
              >
                <option value=''>{t('SelectPhishing')}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name_en}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-4'>
              <label className='form-label'>{t('SelectDepartments')}</label>
              <select
                className='form-select'
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value=''>{t('SelectDepartment')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Button
                className='btn btn-success'
                onClick={fetchData}
                disabled={!selectedPhishing}
              >
                {t('ApplyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && <div className='text-center py-5'>{t('Loading')}...</div>}
      {error && <div className='text-danger text-center py-2'>{error}</div>}

      <div className='row g-4 mb-4'>
        <div className='col-md-6'>
          <Card className='p-3 text-center'>
            <h6>{t('OpenedVsClicked')}</h6>
            <ResponsiveContainer width='100%' height={250}>
              {hasPieData(data.opened_vs_clicked) ? (
                <PieChart>
                  <Pie
                    data={toPieData(data.opened_vs_clicked)}
                    dataKey='value'
                    nameKey='name'
                    outerRadius={80}
                    labelLine={({ cx, cy, midAngle, outerRadius, index }) => {
                      const r = outerRadius + 13;
                      const RADIAN = Math.PI / 180;
                      const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                      const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                      const x2 = cx + r * Math.cos(-midAngle * RADIAN);
                      const y2 = cy + r * Math.sin(-midAngle * RADIAN);
                      return (
                        <line
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke={COLORS[index % COLORS.length]}
                        />
                      );
                    }}
                    label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                      const r = outerRadius + 16;
                      const RADIAN = Math.PI / 180;
                      const x = cx + r * Math.cos(-midAngle * RADIAN);
                      const y = cy + r * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={COLORS[index % COLORS.length]}
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline='central'
                          style={{ fontSize: 14, direction: 'ltr' }}
                        >
                          {value}
                        </text>
                      );
                    }}
                  >
                    {toPieData(data.opened_vs_clicked).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100'>
                  <p className='text-muted mb-0 fs-4'>{t('NA')}</p>
                </div>
              )}
            </ResponsiveContainer>
          </Card>
        </div>

        <div className='col-md-6'>
          <Card className='p-3 text-center'>
            <h6>{t('OpenedVsSubmitted')}</h6>
            <ResponsiveContainer width='100%' height={250}>
              {hasPieData(data.opened_vs_submitted) ? (
                <PieChart>
                  <Pie
                    data={toPieData(data.opened_vs_submitted)}
                    dataKey='value'
                    nameKey='name'
                    outerRadius={80}
                    labelLine={({ cx, cy, midAngle, outerRadius, index }) => {
                      const r = outerRadius + 13;
                      const RADIAN = Math.PI / 180;
                      const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                      const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                      const x2 = cx + r * Math.cos(-midAngle * RADIAN);
                      const y2 = cy + r * Math.sin(-midAngle * RADIAN);
                      return (
                        <line
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke={COLORS[index % COLORS.length]}
                        />
                      );
                    }}
                    label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                      const r = outerRadius + 16;
                      const RADIAN = Math.PI / 180;
                      const x = cx + r * Math.cos(-midAngle * RADIAN);
                      const y = cy + r * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={COLORS[index % COLORS.length]}
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline='central'
                          style={{ fontSize: 14, direction: 'ltr' }}
                        >
                          {value}
                        </text>
                      );
                    }}
                  >
                    {toPieData(data.opened_vs_submitted).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100'>
                  <p className='text-muted mb-0 fs-4'>{t('NA')}</p>
                </div>
              )}
            </ResponsiveContainer>
          </Card>
        </div>
      </div>

      <div className='mb-4'>
        <Card className='p-3 text-center'>
          <h6>{t('DailyOpenedVsNotOpenedLatest')}</h6>
          <ResponsiveContainer width='100%' height={250}>
            {hasPieData(data.daily_opened_vs_not_opened[0]) ? (
              <PieChart>
                <Pie
                  data={toPieData(data.daily_opened_vs_not_opened[0])}
                  dataKey='value'
                  nameKey='name'
                  outerRadius={90}
                  labelLine={({ cx, cy, midAngle, outerRadius, index }) => {
                    const r = outerRadius + 13;
                    const RADIAN = Math.PI / 180;
                    const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                    const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                    const x2 = cx + r * Math.cos(-midAngle * RADIAN);
                    const y2 = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={COLORS[index % COLORS.length]}
                      />
                    );
                  }}
                  label={({ cx, cy, midAngle, outerRadius, index, value, name }) => {
                    const r = outerRadius + 16;
                    const RADIAN = Math.PI / 180;
                    const x = cx + r * Math.cos(-midAngle * RADIAN);
                    const y = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={COLORS[index % COLORS.length]}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline='central'
                        style={{ fontSize: 14, direction: 'ltr' }}
                      >
                        {`${name}: ${value}`}
                      </text>
                    );
                  }}
                >
                  {toPieData(data.daily_opened_vs_not_opened[0]).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            ) : (
              <div className='d-flex align-items-center justify-content-center h-100'>
                <p className='text-muted mb-0 fs-4'>{t('NA')}</p>
              </div>
            )}
          </ResponsiveContainer>
        </Card>
      </div>

      <div className='mb-4'>
        <Card className='p-3'>
          <h6>{t('DailyActivitySummary')}</h6>
          <ResponsiveContainer width='100%' height={300}>
            <LineChart
              data={lineChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey='date' type='category'/>
              <YAxis  orientation={isRtl ? 'right' : 'left'}
                        tick={{
                          dx: isRtl ? -0 : -28,
                          textAnchor: isRtl ? 'end' : 'start',
                        }}/>
              <Tooltip />
              <Legend />
              <Line type='monotone' dataKey='opened' stroke={COLORS[0]} activeDot={{ r: 8 }} />
              <Line type='monotone' dataKey='clicked' stroke={COLORS[1]} />
              <Line type='monotone' dataKey='submitted' stroke={COLORS[2]} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
};

export default PhishingCampaignsProgress;
